/**
 * ============================================================================
 * Iron Meridian - World Map Component (D3 Globe)
 * ============================================================================
 * 
 * Interactive 3D globe map built with D3.js and SVG. This is the primary 
 * interaction surface and the emotional/visual core of the game.
 * 
 * Features:
 * - True globe appearance with D3 geoOrthographic projection
 * - Smooth drag rotation with inertia
 * - Country selection and hover tooltips
 * - Ownership-based coloring with animated transitions
 * - Premium lighting/shading effects
 * - Responsive to container size
 * - Click on country to select and focus
 * 
 * Uses Natural Earth 110m world data for realistic country boundaries.
 * 
 * GLOBE RENDERING CONTRACT:
 * - SVG paths are created ONCE and never re-created during rotation
 * - Each country has a unique ISO-3 ID as data-country-id attribute
 * - Updates occur ONLY via D3 attribute mutations, not React reconciliation
 * - See globeRenderContract.js for integrity guarantees
 */

import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react'
import * as d3 from 'd3'
import CountryTooltip from './CountryTooltip'
import MapOverlayControls, { getOverlayColor, STANCE_COLORS } from './MapOverlayControls'
import { loadWorldGeo } from '../services/geoLoader'
import { useGlobeProjection } from '../hooks/useGlobeProjection'
import { getGlobalGlobeContract } from '../services/globeRenderContract'
import { validateGlobeIntegrity, isDevelopment } from '../services/globeFailFast'

// Player color palette - distinct and professional
const PLAYER_COLORS = {
  player: '#3B82F6',        // Blue - primary player
  ai_aggressive: '#DC2626', // Red - aggressive AI
  ai_defensive: '#059669',  // Green - defensive AI
  ai_economic: '#D97706',   // Amber - economic AI
  ai_opportunist: '#7C3AED', // Purple - opportunist AI
  neutral: '#4B5563',       // Gray - neutral/unclaimed
  ai: {
    aggressive: '#DC2626',
    defensive: '#059669',
    economic: '#D97706',
    opportunist: '#7C3AED'
  }
}

// Globe visual configuration
const GLOBE_CONFIG = {
  oceanColor: '#0c1425',
  landStroke: 'rgba(255, 255, 255, 0.12)',
  landStrokeHover: 'rgba(255, 255, 255, 0.5)',
  landStrokeSelected: '#fbbf24',
  graticuleStroke: 'rgba(255, 255, 255, 0.04)',
  atmosphereColor: 'rgba(100, 150, 255, 0.15)',
}

function WorldMap({ 
  countries, 
  selectedCountry, 
  playerCountry, 
  onCountrySelect,
  diplomacy = null  // Optional diplomacy state for overlays
}) {
  const containerRef = useRef(null)
  const countriesGroupRef = useRef(null)
  const graticuleRef = useRef(null)
  const hasCenteredOnPlayerRef = useRef(false)
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 })
  const [geoData, setGeoData] = useState(null)
  const [hoveredCountry, setHoveredCountry] = useState(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const [previousOwnership, setPreviousOwnership] = useState({})
  const [animatingCountries, setAnimatingCountries] = useState(new Set())
  const [legendCollapsed, setLegendCollapsed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasDragged, setHasDragged] = useState(false)
  const [overlayMode, setOverlayMode] = useState('none')
  const dragDistanceRef = useRef(0)

  // Use globe projection hook
  const {
    projection,
    pathGenerator,
    graticule,
    rotation,
    setRotation,
    isDragging,
    isAnimating,
    hasInertia,
    radius,
    handleDragStart: projDragStart,
    handleDragMove: projDragMove,
    handleDragEnd: projDragEnd,
    rotateTo,
    rotateByAnimated,
    resetView,
    getFeatureCentroid
  } = useGlobeProjection(dimensions.width, dimensions.height, 0.42)

  // Combined flag: disable all path transitions and interactions when globe is moving
  const isGlobeMoving = isDragging || isAnimating || hasInertia

  // Update paths directly via D3 when rotation changes (bypasses React reconciliation)
  useEffect(() => {
    if (!geoData || !countriesGroupRef.current) return
    
    // Update all country paths directly
    const group = d3.select(countriesGroupRef.current)
    group.selectAll('path').each(function() {
      const el = d3.select(this)
      const countryId = el.attr('data-country-id')
      const feature = geoData.countries.features.find(f => f.id === countryId)
      if (feature) {
        const newPath = pathGenerator(feature)
        if (newPath) {
          el.attr('d', newPath)
        }
      }
    })
    
    // Update graticule
    if (graticuleRef.current) {
      d3.select(graticuleRef.current).attr('d', pathGenerator(graticule()))
    }
  }, [rotation, geoData, pathGenerator, graticule])

  // Get country color based on controller/personality or overlay mode
  const getCountryColor = useCallback((countryId) => {
    // Check for overlay mode first
    if (overlayMode !== 'none' && diplomacy) {
      const overlayColor = getOverlayColor(countryId, overlayMode, diplomacy, playerCountry)
      if (overlayColor) return overlayColor
    }
    
    // Default ownership-based coloring
    const country = countries[countryId]
    if (!country) return PLAYER_COLORS.neutral
    if (country.controller === 'player') return PLAYER_COLORS.player
    if (country.personality) {
      const key = `ai_${country.personality}`
      return PLAYER_COLORS[key] || PLAYER_COLORS.neutral
    }
    return PLAYER_COLORS.neutral
  }, [countries, overlayMode, diplomacy, playerCountry])

  // Load geography data
  useEffect(() => {
    loadWorldGeo()
      .then(data => {
        setGeoData(data)
        setIsLoading(false)
      })
      .catch(err => {
        console.error('Failed to load globe data:', err)
        setIsLoading(false)
      })
  }, [])

  // Center on player's country when game starts (only once)
  useEffect(() => {
    if (!geoData || !playerCountry || isLoading) return
    if (hasCenteredOnPlayerRef.current) return // Already centered
    
    // Find the country's feature in geoData
    const countryFeature = geoData.countries.features.find(
      f => f.id === playerCountry || f.properties?.iso_a3 === playerCountry
    )
    
    if (countryFeature) {
      const centroid = getFeatureCentroid(countryFeature)
      if (centroid) {
        hasCenteredOnPlayerRef.current = true // Mark as centered
        // Small delay to ensure rendering is complete
        setTimeout(() => {
          rotateTo(centroid[0], centroid[1], 1200)
        }, 300)
      }
    }
  }, [geoData, playerCountry, isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize render contract after paths are created
  // This captures the initial SVG structure as the immutable baseline
  useEffect(() => {
    if (!geoData || !countriesGroupRef.current || isLoading) return
    
    // Wait for next frame to ensure paths are in DOM
    requestAnimationFrame(() => {
      const contract = getGlobalGlobeContract()
      if (!contract.getStatus().initialized) {
        contract.initialize(countriesGroupRef.current)
        
        // Run initial integrity validation in development
        if (isDevelopment()) {
          validateGlobeIntegrity(countriesGroupRef.current, geoData, countries)
        }
      }
    })
  }, [geoData, isLoading, countries])

  // Handle container resize
  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        setDimensions({ width, height })
      }
    })

    resizeObserver.observe(containerRef.current)
    
    // Initial size
    const rect = containerRef.current.getBoundingClientRect()
    setDimensions({ width: rect.width, height: rect.height })

    return () => resizeObserver.disconnect()
  }, [])

  // Detect ownership changes and trigger animations
  useEffect(() => {
    const newAnimating = new Set()
    
    Object.entries(countries).forEach(([id, country]) => {
      const prevController = previousOwnership[id]
      if (prevController && prevController !== country.controller) {
        newAnimating.add(id)
        setTimeout(() => {
          setAnimatingCountries(prev => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }, 700)
      }
    })

    if (newAnimating.size > 0) {
      setAnimatingCountries(prev => new Set([...prev, ...newAnimating]))
    }

    const newOwnership = {}
    Object.entries(countries).forEach(([id, country]) => {
      newOwnership[id] = country.controller
    })
    setPreviousOwnership(newOwnership)
  }, [countries])

  // Mouse event handlers with drag detection
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      dragDistanceRef.current = 0
      setHasDragged(false)
      projDragStart(e.clientX, e.clientY)
    }
  }, [projDragStart])

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      dragDistanceRef.current += Math.abs(e.movementX) + Math.abs(e.movementY)
      if (dragDistanceRef.current > 5) {
        setHasDragged(true)
      }
    }
    projDragMove(e.clientX, e.clientY)
    
    if (hoveredCountry) {
      setTooltipPos({ x: e.clientX, y: e.clientY })
    }
  }, [projDragMove, hoveredCountry, isDragging])

  const handleMouseUp = useCallback(() => {
    projDragEnd()
    setTimeout(() => setHasDragged(false), 50)
  }, [projDragEnd])

  const handleMouseLeave = useCallback(() => {
    projDragEnd()
    setHoveredCountry(null)
  }, [projDragEnd])

  // Country interaction handlers
  const handleCountryClick = useCallback((countryId, e) => {
    e.stopPropagation()
    if (hasDragged) return
    
    onCountrySelect(countryId)
    
    // Rotate to clicked country
    if (geoData) {
      const feature = geoData.countries.features.find(f => f.id === countryId)
      if (feature) {
        const centroid = getFeatureCentroid(feature)
        if (centroid) {
          rotateTo(centroid[0], centroid[1], 800)
        }
      }
    }
  }, [hasDragged, onCountrySelect, geoData, getFeatureCentroid, rotateTo])

  const handleCountryHover = useCallback((countryId, event) => {
    // Always allow hover - tooltip will show country data or fallback info
    setHoveredCountry(countryId)
    setTooltipPos({ x: event.clientX, y: event.clientY })
  }, [])

  const handleCountryLeave = useCallback(() => {
    setHoveredCountry(null)
  }, [])

  // Render country paths - only re-renders for selection/hover/color changes, NOT rotation
  // Path 'd' attributes are updated directly via D3 in the useEffect above
  const countryPaths = useMemo(() => {
    if (!geoData || !pathGenerator) return null

    return geoData.countries.features.map(feature => {
      const countryId = feature.id
      const path = pathGenerator(feature)
      if (!path) return null

      const isSelected = countryId === selectedCountry
      const isHovered = countryId === hoveredCountry
      const isAnimatingOwnership = animatingCountries.has(countryId)
      const color = getCountryColor(countryId)

      return (
        <path
          key={countryId}
          data-country-id={countryId}
          d={path}
          fill={color}
          stroke={isSelected ? GLOBE_CONFIG.landStrokeSelected : isHovered ? GLOBE_CONFIG.landStrokeHover : GLOBE_CONFIG.landStroke}
          strokeWidth={isSelected ? 2.5 : isHovered ? 1.5 : 0.5}
          strokeLinejoin="round"
          className={`cursor-pointer ${isAnimatingOwnership ? 'animate-ownership-pulse' : ''}`}
          style={{
            filter: isSelected 
              ? 'brightness(1.2) drop-shadow(0 0 8px rgba(251, 191, 36, 0.5))' 
              : isHovered 
                ? 'brightness(1.15) drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))'
                : undefined
          }}
          onClick={(e) => handleCountryClick(countryId, e)}
          onMouseEnter={(e) => handleCountryHover(countryId, e)}
          onMouseLeave={handleCountryLeave}
        />
      )
    })
  }, [geoData, selectedCountry, hoveredCountry, animatingCountries, getCountryColor, handleCountryClick, handleCountryHover, handleCountryLeave])

  // Initial graticule path (updated via D3 in useEffect)
  const initialGraticulePath = useMemo(() => {
    if (!pathGenerator) return ''
    return pathGenerator(graticule()) || ''
  }, [graticule]) // Only recompute when graticule generator changes, not pathGenerator

  // Globe outline path
  const globeOutlinePath = useMemo(() => {
    return pathGenerator({ type: 'Sphere' })
  }, [pathGenerator])

  // Center coordinates
  const center = useMemo(() => ({
    x: dimensions.width / 2,
    y: dimensions.height / 2
  }), [dimensions])

  return (
    <div 
      ref={containerRef} 
      className="relative w-full h-full overflow-hidden map-container"
      style={{ background: 'radial-gradient(ellipse at 50% 50%, #0f172a 0%, #0a0f1a 50%, #030712 100%)' }}
    >
      {/* Subtle star field */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: `
            radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.5), transparent),
            radial-gradient(1px 1px at 20% 50%, rgba(255,255,255,0.4), transparent),
            radial-gradient(1px 1px at 30% 30%, rgba(255,255,255,0.3), transparent),
            radial-gradient(1px 1px at 40% 70%, rgba(255,255,255,0.5), transparent),
            radial-gradient(1px 1px at 50% 10%, rgba(255,255,255,0.4), transparent),
            radial-gradient(1px 1px at 60% 80%, rgba(255,255,255,0.3), transparent),
            radial-gradient(1px 1px at 70% 40%, rgba(255,255,255,0.5), transparent),
            radial-gradient(1px 1px at 80% 60%, rgba(255,255,255,0.4), transparent),
            radial-gradient(1px 1px at 90% 25%, rgba(255,255,255,0.3), transparent),
            radial-gradient(1px 1px at 15% 85%, rgba(255,255,255,0.5), transparent),
            radial-gradient(1px 1px at 85% 90%, rgba(255,255,255,0.4), transparent),
            radial-gradient(1px 1px at 5% 5%, rgba(255,255,255,0.3), transparent)
          `
        }}
      />

      {/* Main SVG Globe */}
      <svg
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className={`absolute inset-0 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          {/* Globe shadow gradient */}
          <radialGradient id="globeShadow" cx="50%" cy="50%" r="50%">
            <stop offset="80%" stopColor="transparent" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.5)" />
          </radialGradient>
          
          {/* Atmosphere glow */}
          <radialGradient id="atmosphere" cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor="rgba(100, 180, 255, 0.15)" />
            <stop offset="50%" stopColor="rgba(60, 130, 200, 0.05)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          
          {/* Globe lighting gradient */}
          <radialGradient id="globeLight" cx="25%" cy="20%" r="80%">
            <stop offset="0%" stopColor="rgba(255, 255, 255, 0.06)" />
            <stop offset="50%" stopColor="transparent" />
            <stop offset="100%" stopColor="rgba(0, 0, 0, 0.12)" />
          </radialGradient>

          {/* Drop shadow filter */}
          <filter id="dropShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="10" stdDeviation="20" floodColor="rgba(0,0,0,0.4)" />
          </filter>

          {/* Glow filter for selection */}
          <filter id="selectionGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#fbbf24" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Clip path for globe - ensures nothing renders outside the sphere */}
          <clipPath id="globeClip">
            <circle cx={dimensions.width / 2} cy={dimensions.height / 2} r={radius} />
          </clipPath>
        </defs>

        {/* Globe shadow beneath */}
        <ellipse
          cx={center.x}
          cy={center.y + radius + 35}
          rx={radius * 0.8}
          ry={radius * 0.12}
          fill="rgba(0,0,0,0.25)"
          style={{ filter: 'blur(25px)' }}
        />

        {/* Atmosphere glow ring */}
        <circle
          cx={center.x}
          cy={center.y}
          r={radius + 15}
          fill="none"
          stroke="rgba(100, 180, 255, 0.12)"
          strokeWidth="30"
          style={{ filter: 'blur(15px)' }}
        />

        {/* Ocean background */}
        <path
          d={globeOutlinePath}
          fill={GLOBE_CONFIG.oceanColor}
          style={{ filter: 'url(#dropShadow)' }}
        />

        {/* Ocean gradient overlay */}
        <path
          d={globeOutlinePath}
          fill="url(#atmosphere)"
        />

        {/* Graticule (grid lines) */}
        <path
          ref={graticuleRef}
          d={initialGraticulePath}
          fill="none"
          stroke={GLOBE_CONFIG.graticuleStroke}
          strokeWidth="0.5"
          clipPath="url(#globeClip)"
        />

        {/* Country paths */}
        <g ref={countriesGroupRef} className="countries" clipPath="url(#globeClip)">
          {countryPaths}
        </g>

        {/* Globe lighting overlay */}
        <path
          d={globeOutlinePath}
          fill="url(#globeLight)"
          pointerEvents="none"
        />

        {/* Globe edge shadow */}
        <path
          d={globeOutlinePath}
          fill="url(#globeShadow)"
          pointerEvents="none"
        />

        {/* Globe rim highlight */}
        <circle
          cx={center.x}
          cy={center.y}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="1"
        />
      </svg>

      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-30 bg-war-room-900/90 backdrop-blur-sm">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-war-room-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-gray-300 font-medium">Initializing Globe</div>
            <div className="text-gray-500 text-sm mt-1">Loading geographic data...</div>
          </div>
        </div>
      )}

      {/* Vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none z-20"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(0,0,0,0.4) 85%, rgba(0,0,0,0.7) 100%)',
          boxShadow: 'inset 0 0 120px rgba(0,0,0,0.4)'
        }}
      />

      {/* Controls */}
      <div className="absolute bottom-6 right-6 flex gap-2 z-30">
        <button
          onClick={() => rotateByAnimated(-30, 0)}
          className="w-11 h-11 bg-war-room-800/95 backdrop-blur-sm hover:bg-war-room-700 rounded-xl flex items-center justify-center text-gray-300 hover:text-white border border-war-room-600/50 shadow-lg transition-all active:scale-95"
          title="Rotate Left"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={() => rotateByAnimated(30, 0)}
          className="w-11 h-11 bg-war-room-800/95 backdrop-blur-sm hover:bg-war-room-700 rounded-xl flex items-center justify-center text-gray-300 hover:text-white border border-war-room-600/50 shadow-lg transition-all active:scale-95"
          title="Rotate Right"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          onClick={() => rotateByAnimated(0, -15)}
          className="w-11 h-11 bg-war-room-800/95 backdrop-blur-sm hover:bg-war-room-700 rounded-xl flex items-center justify-center text-gray-300 hover:text-white border border-war-room-600/50 shadow-lg transition-all active:scale-95"
          title="Tilt Up"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7 7 7" />
          </svg>
        </button>

        <button
          onClick={() => rotateByAnimated(0, 15)}
          className="w-11 h-11 bg-war-room-800/95 backdrop-blur-sm hover:bg-war-room-700 rounded-xl flex items-center justify-center text-gray-300 hover:text-white border border-war-room-600/50 shadow-lg transition-all active:scale-95"
          title="Tilt Down"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m0 0l-7-7m7 7 7-7" />
          </svg>
        </button>

        <button
          onClick={() => {
            // Center on player country if available
            if (playerCountry && geoData) {
              const feature = geoData.countries.features.find(
                f => f.id === playerCountry || f.properties?.iso_a3 === playerCountry
              )
              if (feature) {
                const centroid = getFeatureCentroid(feature)
                if (centroid) {
                  rotateTo(centroid[0], centroid[1])
                  return
                }
              }
            }
            // Fallback to default reset
            resetView()
          }}
          className="w-11 h-11 bg-war-room-800/95 backdrop-blur-sm hover:bg-war-room-700 rounded-xl flex items-center justify-center text-gray-300 hover:text-white border border-war-room-600/50 shadow-lg transition-all active:scale-95"
          title="Center on Home Country"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </button>
      </div>

      {/* Mini-map - positioned under coordinates */}
      <div className="absolute top-16 right-4 z-30">
        {geoData && (
          (() => {
            const miniW = 180
            const miniH = 120
            const miniProj = d3.geoNaturalEarth1()
              .scale(Math.min(miniW, miniH) / 6)
              .translate([miniW / 2, miniH / 2])

            const miniPath = d3.geoPath().projection(miniProj)

            const centerLon = -rotation[0]
            const centerLat = -rotation[1]
            const centerXY = miniProj([centerLon, centerLat])

            const handleMiniClick = (e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const x = e.clientX - rect.left
              const y = e.clientY - rect.top
              const lonLat = miniProj.invert([x, y])
              if (lonLat) rotateTo(lonLat[0], lonLat[1], 800)
            }

            return (
              <svg width={miniW} height={miniH} onClick={handleMiniClick} className="rounded-lg border border-war-room-600/40 bg-war-room-800/80 p-1 cursor-pointer">
                <g>
                  {geoData.countries.features.map(f => {
                    try {
                      const d = miniPath(f)
                      if (!d) return null
                      return (
                        <path key={f.id} d={d} fill={getCountryColor(f.id)} stroke="rgba(255,255,255,0.04)" strokeWidth={0.3} />
                      )
                    } catch (err) {
                      return null
                    }
                  })}
                </g>
                {/* Visible center marker */}
                {centerXY && (
                  <g pointerEvents="none">
                    <circle cx={centerXY[0]} cy={centerXY[1]} r={4} fill="#ffd24a" stroke="#00000055" strokeWidth={0.6} />
                    <circle cx={centerXY[0]} cy={centerXY[1]} r={8} fill="none" stroke="#ffd24a" strokeWidth={0.8} opacity={0.25} />
                  </g>
                )}
              </svg>
            )
          })()
        )}
      </div>

      {/* Collapsible Legend */}
      <div className="absolute bottom-6 left-6 z-30">
        <div className={`bg-war-room-800/95 backdrop-blur-sm rounded-xl border border-war-room-600/50 shadow-lg transition-all duration-200 ${legendCollapsed ? 'w-11' : 'w-auto'}`}>
          <button
            onClick={() => setLegendCollapsed(prev => !prev)}
            className={`w-11 h-11 flex items-center justify-center text-gray-300 hover:text-white hover:bg-war-room-700 transition-all ${legendCollapsed ? 'rounded-xl' : 'rounded-t-xl border-b border-war-room-600/50'}`}
            title={legendCollapsed ? 'Show Legend' : 'Hide Legend'}
          >
            <svg className={`w-5 h-5 transition-transform duration-200 ${legendCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </button>
          
          {!legendCollapsed && (
            <div className="p-4 pt-3">
              {/* Overlay Controls */}
              {diplomacy && (
                <div className="mb-3 pb-3 border-b border-war-room-600/50">
                  <MapOverlayControls
                    activeOverlay={overlayMode}
                    onOverlayChange={setOverlayMode}
                    disabled={isGlobeMoving}
                  />
                </div>
              )}
              
              {/* Dynamic Legend based on overlay mode */}
              {overlayMode === 'stance' ? (
                <>
                  <div className="text-xs font-semibold text-gray-300 mb-3 uppercase tracking-wider">Diplomatic Stance</div>
                  <div className="flex flex-col gap-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: STANCE_COLORS.ALLY }} />
                      <span className="text-green-400 font-medium">Allied</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: STANCE_COLORS.FRIENDLY }} />
                      <span className="text-green-300">Friendly</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: STANCE_COLORS.NEUTRAL }} />
                      <span className="text-gray-400">Neutral</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: STANCE_COLORS.WARY }} />
                      <span className="text-yellow-400">Wary</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: STANCE_COLORS.HOSTILE }} />
                      <span className="text-red-400">Hostile</span>
                    </div>
                  </div>
                </>
              ) : overlayMode === 'alliance' ? (
                <>
                  <div className="text-xs font-semibold text-gray-300 mb-3 uppercase tracking-wider">Alliance Blocs</div>
                  <div className="flex flex-col gap-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: '#3b82f6' }} />
                      <span className="text-blue-400 font-medium">Your Alliance</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: '#ef4444' }} />
                      <span className="text-red-400">Hostile Nations</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: '#6b7280' }} />
                      <span className="text-gray-500">Neutral</span>
                    </div>
                  </div>
                </>
              ) : overlayMode === 'treaties' ? (
                <>
                  <div className="text-xs font-semibold text-gray-300 mb-3 uppercase tracking-wider">Treaty Partners</div>
                  <div className="flex flex-col gap-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: '#8b5cf6' }} />
                      <span className="text-purple-400 font-medium">Treaty Partners</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" style={{ backgroundColor: '#4b5563' }} />
                      <span className="text-gray-500">No Treaty</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs font-semibold text-gray-300 mb-3 uppercase tracking-wider">Territory Control</div>
                  <div className="flex flex-col gap-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-sm shadow-sm ring-1 ring-white/20" 
                        style={{ backgroundColor: PLAYER_COLORS.player }}
                      />
                      <span className="text-gray-200 font-medium">Your Empire</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex gap-0.5">
                        <div className="w-2 h-4 rounded-l-sm" style={{ backgroundColor: PLAYER_COLORS.ai.aggressive }} />
                        <div className="w-2 h-4" style={{ backgroundColor: PLAYER_COLORS.ai.defensive }} />
                        <div className="w-2 h-4" style={{ backgroundColor: PLAYER_COLORS.ai.economic }} />
                        <div className="w-2 h-4 rounded-r-sm" style={{ backgroundColor: PLAYER_COLORS.ai.opportunist }} />
                      </div>
                      <span className="text-gray-400">AI Nations</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-sm shadow-sm" 
                        style={{ backgroundColor: PLAYER_COLORS.neutral }}
                      />
                      <span className="text-gray-500">Neutral</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-sm border-2 border-yellow-400 bg-yellow-400/20 shadow-sm shadow-yellow-400/20" />
                      <span className="text-gray-400">Selected</span>
                    </div>
                  </div>
                </>
              )}
              
              <div className="mt-3 pt-3 border-t border-war-room-600/50 text-xs text-gray-500 flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-war-room-700 rounded text-gray-400">Drag</kbd>
                <span>to rotate globe</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rotation coordinates indicator */}
      <div className="absolute top-4 right-4 z-30">
        <div className="bg-war-room-800/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-war-room-600/50 text-xs font-mono text-gray-400 flex items-center gap-3">
          <span>
            <span className="text-gray-500">λ</span> {rotation[0].toFixed(0)}°
          </span>
          <span>
            <span className="text-gray-500">φ</span> {rotation[1].toFixed(0)}°
          </span>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCountry && !isDragging && (() => {
        // Get country data or create fallback for unknown countries
        const countryData = countries[hoveredCountry] || {
          id: hoveredCountry,
          name: hoveredCountry,
          controller: 'neutral',
          military: 0,
          economy: 0,
          technology: 0,
          stability: 50,
          color: '#6B7280'
        }
        return (
          <CountryTooltip
            country={countryData}
            position={tooltipPos}
            isPlayer={countryData.controller === 'player'}
          />
        )
      })()}
    </div>
  )
}

export default memo(WorldMap)
